import type { ReactNode, JSX } from 'react'

import {
  EmptyState,
  AllChannelsIcon,
  SearchIcon,
  Flex,
  Text,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import { ChannelSelector } from '~publish/components/ChannelSelector'
import type { NormalizedChannel } from '~publish/components/ChannelSelector'

import { FilterButton } from '../FilterButton'

import styles from './FilterByChannel.module.css'

export type FilterByChannelViewItemProps = Readonly<{
  disabled?: boolean
  disabledReason?: ReactNode
  viewOnly?: boolean
  viewOnlyReason?: ReactNode
}>

export type FilterByChannelViewProps = Readonly<{
  channels: readonly NormalizedChannel[]
  value: string[]
  onChange: (channelIds: string[]) => void
  onSelectAll: () => void
  count: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  groups?: ReactNode
  emptyState?: ReactNode
  footer?: ReactNode
  getItemProps?: (channel: NormalizedChannel) => FilterByChannelViewItemProps
}>

export const FilterByChannelView = ({
  channels,
  value,
  onChange,
  onSelectAll,
  count,
  open,
  onOpenChange,
  className,
  groups,
  emptyState,
  footer,
  getItemProps,
}: FilterByChannelViewProps): JSX.Element => (
  <ChannelSelector
    value={value}
    onChange={onChange}
    open={open}
    onOpenChange={onOpenChange}
  >
    <ChannelSelector.Trigger>
      <FilterButton
        size="medium"
        icon={<AllChannelsIcon />}
        count={count}
        variant="tertiary"
        className={className}
      >
        Channels
      </FilterButton>
    </ChannelSelector.Trigger>

    <ChannelSelector.Content
      className={styles.content}
      align="end"
      // Each pick navigates + refetches the list behind the popover; that
      // async re-render can move focus out and make Radix auto-dismiss. Keep
      // it open for multi-select — pointer-outside and Escape still close it.
      onFocusOutside={(event): void => event.preventDefault()}
    >
      <ChannelSelector.Search placeholder="Search channels" />
      <ChannelSelector.List>
        {groups}
        <Flex justify="between" align="center" className={styles.selectAll}>
          <Text>Channels</Text>
          <UnstyledButton onClick={onSelectAll}>
            <Text>{value.length ? 'Unselect all' : 'Select all'}</Text>
          </UnstyledButton>
        </Flex>

        {/* Rendered directly, not via ChannelSelector.Empty: cmdk only shows
            its own Empty state when the whole list's filtered item count is
            zero, but "No channels" is a registered, always-matching item
            whenever `showNoChannelsOption` is set — so that count never
            reaches zero even when there are no real channels. */}
        {channels.length === 0 && emptyState}
        {channels.length > 0 && (
          <ChannelSelector.Empty>
            <EmptyState size="small" translate="no">
              <EmptyState.Icon>
                <SearchIcon />
              </EmptyState.Icon>
              <EmptyState.Heading>No channels found</EmptyState.Heading>
              <EmptyState.Description>
                Try a different search or clear the search to see all channels.
              </EmptyState.Description>
            </EmptyState>
          </ChannelSelector.Empty>
        )}
        {channels.map((channel) => (
          <ChannelSelector.Item
            key={channel.id}
            value={channel.id}
            channel={channel}
            {...getItemProps?.(channel)}
            keywords={[
              channel.name,
              channel.service,
              channel.serviceType,
              channel.locationData,
              channel.serverUrl,
            ].filter((keyword): keyword is string => Boolean(keyword))}
          />
        ))}
        {footer}
      </ChannelSelector.List>
    </ChannelSelector.Content>
  </ChannelSelector>
)
